import {
  ArrowRightIcon,
  Button,
  CaretRightIcon,
  PencilSimpleIcon,
  ProgressBar,
} from "@stacklet/ui";
import { generatePath } from "react-router";

import { Link, TableLink } from "app/components/links";
import { paths } from "app/constants/paths";

import { type GroupSection, type Group } from "./groupSections";
import { SectionIcon } from "./SectionIcon";

interface GroupSectionCellProps {
  /** The section the row represents. */
  section: GroupSection;
  /** Whether the section's groups are loading (shows a thin bar at the base). */
  isLoading: boolean;
}

/** The full-width section header: icon, name, and its row actions. */
export function GroupSectionCell({
  section,
  isLoading,
}: GroupSectionCellProps) {
  return (
    <div className="relative flex h-full w-full items-center justify-between">
      {isLoading ? (
        // Pull out past the cell's px-xs padding so the bar spans the full section.
        <div className="absolute -inset-x-xs bottom-0">
          <ProgressBar size="slim" indeterminate />
        </div>
      ) : null}
      <div className="flex items-center gap-sm">
        <SectionIcon idpType={section.idpType} />
        <span className="text-label-medium-strong text-text-primary">
          {section.label}
        </span>
        {section.secondary ? (
          <span className="text-label-small text-text-secondary">
            {section.secondary}
          </span>
        ) : null}
      </div>
      {/* Same gap as the group rows' actions column (the buttons' own p-md
          gives the hit area and right-edge spacing), so the section's edit/arrow
          line up with the group actions below it. */}
      <div className="flex items-center gap-xxs">
        {section.editTo ? (
          <Link
            className="inline-flex p-md text-text-secondary hover:text-text-primary"
            textDecoration="none"
            to={section.editTo}
          >
            <PencilSimpleIcon aria-label={`Edit ${section.label}`} size={16} />
          </Link>
        ) : null}
        {section.to ? (
          <Link
            className="inline-flex p-md text-text-secondary hover:text-text-primary"
            textDecoration="none"
            to={section.to}
          >
            <ArrowRightIcon aria-label={`View ${section.label}`} size={16} />
          </Link>
        ) : null}
      </div>
    </div>
  );
}

interface DisclosureToggleProps {
  /** Whether the group's panel is open. */
  isExpanded: boolean;
  /** The group's label, for the toggle's accessible name. */
  label: string;
  /** Toggles the group's panel open/closed. */
  onToggle: () => void;
}

/** The caret button in a group row's leading disclosure column. */
export function DisclosureToggle({
  isExpanded,
  label,
  onToggle,
}: DisclosureToggleProps) {
  return (
    <Button
      IconLeft={
        <CaretRightIcon
          className={isExpanded ? "rotate-90" : undefined}
          size={16}
        />
      }
      aria-expanded={isExpanded}
      aria-label={`${isExpanded ? "Collapse" : "Expand"} ${label}`}
      className="text-text-secondary hover:text-text-primary"
      onPress={onToggle}
      type="button"
      variant="plain"
    />
  );
}

interface GroupNameCellProps {
  /** The group shown in the cell. */
  group: Group;
}

/** A group row's name cell: provider icon, linked name, and origin line. The
 * disclosure caret lives in its own leading column, not here. */
export function GroupNameCell({ group }: GroupNameCellProps) {
  return (
    <div className="flex h-full min-w-0 items-center gap-sm">
      <SectionIcon idpType={group.provider?.idpType} />
      <div className="leading-tight min-w-0">
        <TableLink
          name={group.label}
          to={generatePath(paths.groupDetails, { groupUuid: group.uuid })}
        />
        <div className="truncate text-label-small text-text-secondary">
          {group.provider ? `Synced from ${group.provider.label}` : "Local"}
        </div>
      </div>
    </div>
  );
}
